
import React, { useState, useEffect } from 'react'
import axios from 'axios';
import {baseUrl, limit} from "../../../comman/config";
import { can } from "../../../comman/permissions";
import { useNavigate, Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import Loader from "../../../components/Loader";
import { menuAboveTable } from "../../../comman/selectMenu";
// import Pagination from "react-js-pagination";

function List() {

    const [ingredientList, setIngredientList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [categoryList, setCategoryList] = useState([]);
    const [page, setPage] = useState(1);
    const [totalItem, setTotalItem] = useState(0);
    const [popupOpen, setPopupOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [category, setCategory] = useState(null);
    const [storeList, setStoreList] = useState([]);
    const [store, setStore] = useState(null);
    // Status defaults to Active, matching the other master lists. '' is All.
    const [statusFilter, setStatusFilter] = useState({ value: '1', label: 'Active' });

    // role 0 = superadmin, 1 = admin. Both may enable and disable; only the superadmin may delete.
    const role = String(localStorage.getItem('userRole'));
    // Which buttons this user gets is Role Master's call now, not a role number's -- see
    // comman/permissions.js. `role` is still read because it is what the delete and enable
    // endpoints are told to check against.
    const canDisable = can('disable');
    const canActivate = can('active');
    const canDelete = can('delete');


    const navigate = useNavigate();

    useEffect(() => {
        getUsers();
        getIngredientCategories();
        getStores();
        
    }, []);

    // The text box matches the ingredient's own two names and nothing else. It used to also match
    // category, store and unit, which cannot stand beside dedicated dropdowns for those: typing one
    // store's name while the Store select held another would have been two filters contradicting
    // each other, and the narrower one silently winning.
    //
    // ingredientList is a dependency too -- the status filter starts at Active, so a list seeded
    // straight from the response would show every disabled ingredient until a filter was touched.
    useEffect(() => {
        const query = searchQuery.trim().toLowerCase();
        const status = statusFilter?.value ?? '';
        setFilteredList(ingredientList.filter((item) => {
            const matchesName = !query
                || (item.ingredient ?? '').toLowerCase().includes(query)
                || (item.eng_name ?? '').toLowerCase().includes(query);
            const matchesCategory = !category || category.value === 'all'
                || (item.category && item.category.id === category.value);
            const matchesStore = !store || store.value === 'all'
                || (item.store && item.store.id === store.value);
            const matchesStatus = status === '' || String(item.status) === status;
            return matchesName && matchesCategory && matchesStore && matchesStatus;
        }));
    }, [searchQuery, category, store, statusFilter, ingredientList]);
    const getUsers = async () => {
        // try/finally: the spinner this sets is drawn now, and a request that fails -- the case a
        // spinner exists for -- must not leave it turning over an empty table for good.
        setPopupOpen(true);
        try {
            const response = await axios.get(`${baseUrl}/ingredients/list?page=${page}&limit=${limit}`);
            setIngredientList(response.data.data);
            setTotalItem(response.data.totalItem);
        } catch (error) {
            console.log(error);
            alert('Could not load the list. Check the connection and try again.');
        } finally {
            setPopupOpen(false);
        }
    }

    const getIngredientCategories = async () => {
        const response = await axios.get(`${baseUrl}/ingredients/category/list?condition=status=1`);
        const list = response.data.data;
        setCategoryList(list);
    };

    const getStores = async () => {
        const response = await axios.get(`${baseUrl}/comman/list?table=store&condition=status=1`);
        setStoreList(response.data.data || []);
    };

    const handelFilterChange= (e) => {
        const { value } = e.target;
        setSearchQuery(value);
    };

    const handleSelectChange = (selectedOption) => {
        setCategory(selectedOption);
    };

    const handleClear = () => {
        setSearchQuery('');
        setCategory(null);
        setStore(null);
        setStatusFilter({ value: '1', label: 'Active' });
    };

    const deleteIngredient = async (id) => {
        const shouldDelete = window.confirm("Are you sure you want to disable this ingredient?");
        if (shouldDelete) {
            try {
                await axios.delete(`${baseUrl}/ingredients/delete/${id}`, { data: { role } });
                window.location.reload();
            } catch (error) {
                alert(error?.response?.data?.msg ?? 'Could not disable the ingredient.');
            }
        }
    };

    const enableIngredient = async (id) => {
        if (!window.confirm("Enable this ingredient?")) return;
        try {
            await axios.post(`${baseUrl}/ingredients/enable/${id}`, { role });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not enable the ingredient.');
        }
    };

    // Delete removes the row, unlike Disable which is recoverable, so its confirmation says so.
    // Whether a recipe or a production record still names the ingredient is the backend's call --
    // it counts them and refuses with the reason, which is what gets shown here.
    const destroyIngredient = async (id) => {
        if (!window.confirm("Permanently DELETE this ingredient? Disable is reversible; this is not.")) return;
        try {
            await axios.delete(`${baseUrl}/ingredients/destroy/${id}`, { data: { role } });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not delete the ingredient.');
        }
    };

    const editIngredient = async (id) => {
        navigate(`/ingredients/edit/${btoa(id)}`, { state: ingredientList.filter(item => item.id == id) });
    }

    const handleExport = () => {
        const header = ["Ingredient name", "Category", "Store", "Unit", "Status"];
        const data =   filteredList.map((item) => {
            let resultArray = [];
            resultArray.push(`${item.ingredient} (${item.eng_name})`);
            resultArray.push(`${item?.category ? `${item.category.name} (${item.category.eng_name})` : "-"}`)
            resultArray.push(`${item.store?.name ?? "-"}`)
            resultArray.push(`${item.unit?.name ?? "-"}`)
            // Last column, per spec. Spelled out rather than exported as 1/0 -- the sheet is read
            // away from the screen that would explain which is which.
            resultArray.push(item.status == 1 ? "Active" : "Inactive")

            return resultArray;
        })
        
        
        const worksheet = XLSX.utils.aoa_to_sheet([header, ...data]);

        // Print-ready, within what the xlsx community build actually writes. Bold, borders, fills,
        // wrap text, frozen panes and page setup are Pro features: the free build accepts a cell's
        // `s` and drops it silently. So this sets the things that do reach the file -- column
        // widths from the longest value in each column, a taller header row, and narrow margins --
        // exactly as the Menu Report's export does.
        const columnWidth = (index) => {
            const cells = [header[index], ...data.map((row) => row[index])];
            const longest = cells.reduce((max, cell) => Math.max(max, String(cell ?? '').length), 0);
            return { wch: Math.min(Math.max(longest + 2, 12), 60) };
        };
        worksheet['!cols'] = header.map((_, index) => columnWidth(index));
        worksheet['!rows'] = [{ hpt: 20 }];
        worksheet['!margins'] = { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 };

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Ingredient List");

        XLSX.writeFile(workbook, `Ingredient_list.xlsx`);
    }


    return (
        <div className="p-4">
            {popupOpen && <Loader />}
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">Ingredient Master</h1>
                <div>
                    <button onClick={handleExport} className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Export</button>
                    <Link to="/ingredients/add">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Add Ingredient</button>
                    </Link>
                </div>
            </div>

            {/* Search & Select Section */}
            <div className="pb-4 flex flex-wrap items-center space-x-4">
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center ps-3 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path>
                        </svg>
                    </div>
                    <input type="text" value={searchQuery} onChange={handelFilterChange} id="table-search" className="block p-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg w-80 bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" placeholder="Ingredient Name" />
                </div>
                <Select
                    {...menuAboveTable}
                    isMulti={false}
                    classNamePrefix="select"
                    className="w-48"
                    value={category}
                    name="category"
                    onChange={handleSelectChange}
                    options={[
                        { value: 'all', label: 'All Categories' },
                        ...categoryList.map(cat => ({
                            value: cat.id,
                            label: `${cat.name} ${cat.eng_name ? `(${cat.eng_name})` : ''}`
                        }))
                    ]}
                    placeholder="Ingredient Category"
                    filterOption={(option, inputValue) => {
                        return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
                    }}
                />
                <Select
                    {...menuAboveTable}
                    isMulti={false}
                    classNamePrefix="select"
                    className="w-48"
                    value={store}
                    name="store"
                    onChange={(selected) => setStore(selected)}
                    options={[
                        { value: 'all', label: 'All Stores' },
                        ...storeList.map((st) => ({ value: st.id, label: st.name }))
                    ]}
                    placeholder="Store"
                    filterOption={(option, inputValue) => {
                        return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
                    }}
                />
                <Select
                    {...menuAboveTable}
                    classNamePrefix="select"
                    className="w-36"
                    value={statusFilter}
                    name="status"
                    onChange={(selected) => setStatusFilter(selected)}
                    options={[
                        { value: '', label: 'All' },
                        { value: '1', label: 'Active' },
                        { value: '0', label: 'Inactive' }
                    ]}
                    placeholder="Status"
                    isClearable={false}
                />
                <button
                    onClick={handleClear}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                >
                    Clear
                </button>
            </div>

            {/* List Section */}
            <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                        <tr>
                            <th scope="col" className="px-6 py-3">Ingredient name</th>
                            <th scope="col" className="px-6 py-3">Category</th>
                            <th scope="col" className="px-6 py-3">Store</th>
                            <th scope="col" className="px-6 py-3">Unit</th>
                            <th scope="col" className="px-6 py-3">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredList.map((item) => (
                            <tr key={item.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">{item.ingredient} ({item.eng_name})</th>
                                <td className="px-6 py-4">{item?.category ? `${item.category.name} (${item.category.eng_name})` : "-"}</td>
                                <td className="px-6 py-4">{item.store?.name ?? "-"}</td>
                                <td className="px-6 py-4">{item.unit?.name ?? "-"}</td>
                                <td className="px-6 py-4">
                                <div className="flex gap-2">
                                    {item.status == 1 && <button
                                        type="button"
                                        onClick={() => editIngredient(item.id)}
                                        className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">
                                        Edit
                                    </button>}
                                    {item.status == 1 && canDisable && <button
                                        type="button"
                                        onClick={() => deleteIngredient(item.id)}
                                        className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring focus:ring-red-200">
                                        Disable
                                    </button>}
                                    {item.status == 0 && canActivate && <button
                                        type="button"
                                        onClick={() => enableIngredient(item.id)}
                                        className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring focus:ring-green-200">
                                        Enable
                                    </button>}
                                    {canDelete && <button
                                        type="button"
                                        onClick={() => destroyIngredient(item.id)}
                                        className="px-3 py-1 text-sm bg-red-700 text-white rounded-md hover:bg-red-800 focus:outline-none focus:ring focus:ring-red-300">
                                        Delete
                                    </button>}
                                </div>
                                </td>
                            </tr>))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default List;